import React from 'react';

import { Box } from '../Box';
import { Icon } from '../Icon';
import { Spinner } from '../Spinner';
import { Heading, Text } from '../Typography';
import {
  activeTextStyles,
  cardStyles,
  estimatedTimeStyles,
  pendingCircleStyles,
  stepRowStyles,
} from './ThinkingSteps.styles';

export type ThinkingStepStatus = 'pending' | 'active' | 'completed';

export type ThinkingStep = {
  /**
   * Display text for the step
   */
  text: string;
  /**
   * Current status of the step
   */
  status: ThinkingStepStatus;
};

export type ThinkingStepsProps = {
  /**
   * Array of thinking steps with text and status
   */
  steps: ThinkingStep[];
  /**
   * Optional title displayed above the card
   */
  title?: string;
  /**
   * Optional text displayed below the card (e.g. "This usually takes ~8-10 seconds.")
   */
  estimatedTime?: string;
  /**
   * Additional CSS class names
   */
  className?: string;
};

const StepIcon = ({ status }: { status: ThinkingStepStatus }) => {
  switch (status) {
    case 'completed':
      return (
        <Icon
          variant="CircleCheckSolid"
          size="medium"
          fill="var(--icon-action)"
        />
      );
    case 'active':
      return <Spinner size="medium" fill="var(--icon-action)" />;
    case 'pending':
      return <Box css={pendingCircleStyles} />;
  }
};

const stepTextColor: Record<ThinkingStepStatus, string> = {
  completed: 'var(--text-primary)',
  active: 'var(--color-neutral-500)',
  pending: 'var(--color-neutral-400)',
};

const stepTextWeight: Record<ThinkingStepStatus, 'bold' | 'normal'> = {
  completed: 'bold',
  active: 'normal',
  pending: 'normal',
};

const ThinkingSteps = ({
  steps,
  title,
  estimatedTime,
  className,
}: ThinkingStepsProps) => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="var(--spacing-3)"
      className={className}
    >
      {title && (
        <Heading size="2xs" fontWeight="bold" color="var(--text-primary)">
          {title}
        </Heading>
      )}

      <Box css={cardStyles}>
        {steps.map((step, index) => (
          <Box key={index} css={stepRowStyles}>
            <StepIcon status={step.status} />
            <Text
              size="md"
              fontWeight={stepTextWeight[step.status]}
              color={stepTextColor[step.status]}
              css={step.status === 'active' ? activeTextStyles : undefined}
            >
              {step.text}
            </Text>
          </Box>
        ))}
      </Box>

      {estimatedTime && (
        <Text
          size="sm"
          color="var(--color-neutral-500)"
          css={estimatedTimeStyles}
        >
          {estimatedTime}
        </Text>
      )}
    </Box>
  );
};

export default ThinkingSteps;
